import { useEffect, useState, type JSX } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { ASSET_CATALOG, catalogByGroup } from '../assets/catalog';
import { FOR_MAXIM_SECTION, type ExampleRecord } from './api';
import { EXAMPLE_ASSET_PREFIX } from './ticketsApi';

const GROUPS = catalogByGroup();
const TABS = ['All', ...GROUPS.map((g) => g.group), FOR_MAXIM_SECTION];

// One selectable card: catalog assets and For Maxim uploads share this shape.
type PickerItem = { key: string; label: string; group: string };

// Modal sprite picker: category tabs + name search + a thumbnail grid where one
// or several assets can be toggled (capped at `max`). Selection is local until
// Done. Used by the ticket create form.
export function AssetPickerModal(props: {
  initial: string[];
  max: number;
  // For Maxim uploads offered next to the catalog (linked as example:<id> keys).
  forMaxim: ExampleRecord[];
  assetThumb: (key: string) => { url: string; isImage: boolean };
  onClose: () => void;
  onConfirm: (keys: string[]) => void;
}): JSX.Element {
  const { initial, max, forMaxim, assetThumb, onClose, onConfirm } = props;
  const [selected, setSelected] = useState<string[]>(initial);
  const [tab, setTab] = useState('All');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const allItems: PickerItem[] = [
    ...ASSET_CATALOG.map((e) => ({ key: e.key, label: e.label, group: e.group })),
    ...forMaxim.map((ex) => ({
      key: EXAMPLE_ASSET_PREFIX + ex.id,
      label: ex.name,
      group: FOR_MAXIM_SECTION,
    })),
  ];

  const q = query.trim().toLowerCase();
  const items = allItems.filter(
    (e) =>
      (tab === 'All' || e.group === tab) &&
      (q === '' || e.label.toLowerCase().includes(q) || e.key.toLowerCase().includes(q)),
  );

  const atMax = selected.length >= max;
  const toggle = (key: string): void => {
    setSelected((cur) => {
      if (cur.includes(key)) return cur.filter((k) => k !== key);
      if (cur.length >= max) return cur;
      return [...cur, key];
    });
  };

  return (
    <RemoveScroll>
      <div className="picker-overlay" onClick={onClose}>
        <div className="picker-panel" onClick={(e) => e.stopPropagation()}>
          <div className="picker-header">
            <strong>Select sprites</strong>
            <span className="muted">
              {selected.length}/{max}
            </span>
            <div className="picker-search-wrap">
              <i className="fas fa-search" />
              <input
                className="picker-search"
                placeholder="Search by name..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <button className="modal-close" onClick={onClose} title="Close">
              <i className="fas fa-times" />
            </button>
          </div>

          <div className="picker-tabs">
            {TABS.map((t) => (
              <button
                key={t}
                className={`picker-tab ${t === tab ? 'active' : ''}`}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="picker-grid">
            {items.map((entry) => {
              const isSel = selected.includes(entry.key);
              const thumb = assetThumb(entry.key);
              return (
                <button
                  key={entry.key}
                  type="button"
                  className={`picker-card ${isSel ? 'selected' : ''}`}
                  disabled={!isSel && atMax}
                  onClick={() => toggle(entry.key)}
                  title={entry.key}
                >
                  {thumb.isImage ? (
                    <img className="picker-thumb" src={thumb.url} alt="" loading="lazy" />
                  ) : (
                    <span className="picker-thumb audio">audio</span>
                  )}
                  <span className="picker-name">{entry.label}</span>
                </button>
              );
            })}
            {items.length === 0 && <div className="muted">No matches.</div>}
          </div>

          <div className="picker-footer">
            <button className="primary" onClick={() => onConfirm(selected)}>
              Done ({selected.length})
            </button>
          </div>
        </div>
      </div>
    </RemoveScroll>
  );
}
